import React from 'react';
import { css } from 'styled-components';
import CustomMedia from 'util/custom-media-query';
import { applyStyles } from 'util/helpers';
import Image from 'components/common/Image';
import BlockHeaderCenter from 'components/common/BlockHeaderCenter';
import Colors from 'constants/colors';
import FontStyles from 'constants/font-styles';
import brands from '../constants/brands';
import Pin from './Pin';
import { chain } from 'lodash';
import MapLegend from './MapLegend';
import Media from 'react-media';
import MediaBorders from 'constants/media-borders';

import imgMapPC1x from 'assets/images/PC/@1x/img_map.png';
import imgMapPC2x from 'assets/images/PC/@2x/img_map@2x.png';
import imgMapSP2x from 'assets/images/SP/@2x/img_map@2x.png';
import imgMapSP3x from 'assets/images/SP/@3x/img_map@3x.png';

class Map extends React.Component {
  constructor(props) {
    super(props);

    this.pins = this.getPins(brands);
    this.pinGroups = chain(this.pins)
      .sortBy(Math.random)
      .chunk(Math.ceil(this.pins.length / 6))
      .value();
    this.state = this.pins.reduce(
      (temp, pin) => ({
        ...temp,
        [this.getPinId(pin)]: {
          visible: false,
          group: null
        }
      }),
      {}
    );
  }

  render() {
    const { className } = this.props;

    return (
      <div className={className}>
        <BlockHeaderCenter>宿泊ギフト券が使える宿</BlockHeaderCenter>
        <div className="map">
          <Image
            pc1x={imgMapPC1x}
            pc2x={imgMapPC2x}
            sp2x={imgMapSP2x}
            sp3x={imgMapSP3x}
            alt="img_map"
          />
          <div className="pins">     
            {this.pins.map(pin => (
              <Media
                key={this.getPinId(pin)}
                query={`(min-width: ${MediaBorders.umedium})`}
                children={matches =>
                  matches
                    ? this.renderPin(pin, this.getPinPCPosition(pin))
                    : this.renderPin(pin, this.getPinSPPosition(pin))
                }
              />
            ))}
          </div>
        </div>
        <p className="tips">
          ※ 現在、海外の宿では宿泊ギフト券をご利用いただけません。
        </p>
        <MapLegend />
      </div>
    );
  }

  getPins = brands =>
    brands
      .reduce(
        (temp, brand) =>
          temp.concat(
            brand.facilities.map(facility => ({
              ...facility,
              brand: brand.name
            }))
          ),
        []
      )
      .filter(pin => pin.coordinates != null);

  setPinsVisible = () => {
    const state = {};

    this.pinGroups.forEach((group, index) => {
      group.forEach(pin => {
        state[this.getPinId(pin)] = {
          visible: true,
          group: index
        };
      });
    });

    this.setState(state);
  };

  getPinId(pin) {
    return `${pin.brand}_${pin.name}`;
  }

  getPinContainerClass = pin => {
    const { visible, group } = this.state[this.getPinId(pin)];
    const { brand, name } = pin;
    const classes = ['pin-container'];

    classes.push(visible ? 'visible' : null);
    classes.push(brand ? `brand-${brand}` : null);
    classes.push(group != null ? `group-${group}` : null);
    classes.push(name === 'taketomijima' ? 'taketomijima' : null);

    return classes.filter(Boolean).join(' ');
  };

  renderPin = (pin, pinPosition) => (
    <div className={this.getPinContainerClass(pin)} style={pinPosition}>
      <Pin className="pin" brand={pin.brand} />
    </div>
  );

  getPinPCPosition(pin) {
    const {
      coordinates: { pc }
    } = pin;

    return {
      top: `calc(${pc.y} + 50px)`,
      left: `calc(${pc.x} + 31px)`
    };
  }

  getPinSPPosition(pin) {
    const {
      coordinates: { sp }
    } = pin;

    return {
      top: `calc(${sp.y} + 25px)`,
      left: `calc(${sp.x} + 12px)`
    };
  }
}

const commonStyle = css`
  text-align: center;
  padding-bottom: 28px;

  .tips {
    margin-top: 14px;
    ${FontStyles.text8};
    color: ${Colors.black1};
  }

  .map {
    position: relative;
  }

  .cover {
    ${FontStyles.textSerifSmall};
    position: absolute;
    left: 0;
    bottom: 0;
    width: 120px;
    height: 20px;
    background-color: white;
  }
  
  .pins,
  .pin-container {
    position: absolute;
  }

  .pin-container {
    opacity: 0;
    visibility: hidden;
    transform: translate(-50%, calc(-100% - 12px));

    &.visible {
      animation-name: pin-fall;
      animation-duration: 0.5s;
      animation-iteration-count: 1;
      animation-fill-mode: forwards;
      animation-timing-function: ease-out;
    }

    &.brand-hoshinoya {
      z-index: 5;
    }

    &.brand-kai {
      z-index: 4;
    }

    &.brand-risonare {
      z-index: 3;
    }

    &.brand-omo {
      z-index: 2;
    }

    &.brand-other {
      z-index: 1;
    }

    &.group-0 {
      animation-delay: 0s;
    }

    &.group-1 {
      animation-delay: 0.16s;
    }

    &.group-2 {
      animation-delay: 0.33s;
    }

    &.group-3 {
      animation-delay: 0.5s;
    }

    &.group-4 {
      animation-delay: 0.66s;
    }

    &.group-5 {
      animation-delay: 0.83s;
    }
  }

  .pins {
    top: 0;
    right: 0;
    left: 0;
    margin: auto;
  }

  @keyframes pin-fall {
    from {
      opacity: 0;
      visibility: hidden;
      transform: translate(-50%, calc(-100% - 12px));
    }

    35% {
      opacity: 1;
      visibility: visible;
    }

    to {
      opacity: 1;
      visibility: visible;
      transform: translate(-50%, -100%);
    }
  }
`;

const pcStyle = CustomMedia.greaterThan('umedium')`
  position: relative;

  .map {
    margin-top: -150px;
  }

  .pins {
    width: 1046px;
    height: 718px;
  }

  ${MapLegend} {
    position: absolute;
    right: 80px;
    bottom: 114px;
  }
`;

const spStyle = CustomMedia.lessThan('medium')`
  padding: 0 16px 48px;

  ${BlockHeaderCenter} {
    margin-bottom: 0;
  }

  .map {
    max-width: 343px;
    margin: 0 auto;

    img {
      width: 100%;
    }
  }

  .cover {
    width: 27%;
    height: 30px;
    bottom: 42%;
    white-space: normal;
    word-wrap: break-word;
  }

  .pins {
    width: 100%;
    height: 100%;
  }

  .tips {
    text-align: left;
  }
  .spBr::before {
    content: "\\A";
    white-space: pre;
  }
`;

export default applyStyles(Map)(commonStyle, pcStyle, spStyle);



// WEBPACK FOOTER //
// ./src/components/Home/components/Map.js